import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Badge } from '@/shared/components/ui/badge';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useSubmissionRawValues } from '../../hooks/useSubmissionRawValues';
import type { FormFieldType } from '../../types';

export interface FieldResponsesTarget {
  fieldId: string;
  label: string;
  fieldType: FormFieldType;
  /**
   * For `Select`/`MultiSelect`/`Checkbox`: the specific option to filter to.
   * For `Grid`: `"row column"`. Omitted for open-ended types (shows all responses).
   */
  filterOption?: string;
  /** Human-readable version of `filterOption` for the dialog title, e.g. "Comunicacion / Bueno". */
  filterLabel?: string;
}

interface FieldResponsesDialogProps {
  formId: string;
  target: FieldResponsesTarget | null;
  onOpenChange: (open: boolean) => void;
}

function matchesFilter(fieldType: FormFieldType, rawValue: string, filterOption?: string): boolean {
  if (!filterOption) return true;
  if (fieldType === 'MultiSelect') {
    return rawValue
      .split(',')
      .map((v) => v.trim())
      .includes(filterOption);
  }
  if (fieldType === 'Checkbox') {
    const label = rawValue === 'true' ? 'Checked' : 'Unchecked';
    return label === filterOption;
  }
  if (fieldType === 'Grid') {
    const [row, column] = filterOption.split(' ');
    try {
      const parsed = JSON.parse(rawValue) as Record<string, string>;
      return parsed[row] === column;
    } catch {
      return false;
    }
  }
  return rawValue === filterOption;
}

export function FieldResponsesDialog({ formId, target, onOpenChange }: FieldResponsesDialogProps) {
  const { t } = useTranslation('forms');
  const { submissions, isLoading } = useSubmissionRawValues(formId, !!target);

  const rows = target
    ? submissions
        .map((s) => ({
          submission: s,
          value: s.values.find((v) => v.fieldId === target.fieldId)?.value,
        }))
        .filter(
          (r): r is { submission: (typeof submissions)[number]; value: string } =>
            !!r.value?.trim() && matchesFilter(target.fieldType, r.value, target.filterOption),
        )
    : [];

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onOpenChange(false)}>
      <DialogContent
        className="w-full h-full max-w-none rounded-none md:h-auto md:max-h-[80vh] md:max-w-lg md:rounded-lg overflow-y-auto"
        data-testid="forms-field-responses-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {target?.label}
            {target?.filterLabel ? ` (${target.filterLabel})` : ''}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2" aria-hidden="true">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t('submissions.fieldResponses.empty')}
          </p>
        ) : (
          <ul className="space-y-1.5" data-testid="forms-field-responses-list">
            {rows.map(({ submission, value }) => (
              <li
                key={submission.id}
                className="flex items-center justify-between gap-3 rounded-md border px-3 py-2"
              >
                <span className="text-sm">
                  {submission.isAnonymous ? (
                    <Badge variant="outline">{t('submissions.table.anonymous')}</Badge>
                  ) : (
                    submission.submittedByEmail
                  )}
                </span>
                <span className="text-sm text-muted-foreground truncate max-w-[55%]">{value}</span>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
